import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_ROUTES, APP_ROUTES as ROUTES } from '@app/app.routes';
import { EERROR_TYPE } from '@app/core/models/error.model';
import {
  CARD_TYPE,
  CreateCardToken,
  CreditCardInfo,
  CreditCardTypeResponse,
} from '@app/core/models/openpay.model';
import { ICreditCard, IPaymentParams, IPaymentResponse } from '@app/core/models/payments.model';
import { EVENTS } from '@app/data/events/events-by-category';
import { PaymentsAPI } from '@app/data/mo-api/payments.api';
import { AppAPI } from '@app/data/mo-api/app.api';
import { ERROR_CONSTANTS } from '@app/domain/services/error/error.constants';
import { MapErrorService } from '@app/domain/services/error/map-error.service';
import { OpenpayService } from '@app/domain/services/openpay.service';
import { PaymentService } from '@app/domain/services/payments/payment.service';
import { AppFacade } from '@app/facades/app.facade';
import { ErrorFacade } from '@app/facades/error.facade';
import { PaymentsFacade } from '@app/facades/payments.facade';
import { UtilService } from '@app/services/util.service';
import { environment as ENV } from '@environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as appSelectors from '@store/app/app.selectors';
import * as customerSelectors from '@store/customer/customer.selectors';
import { of } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as paymentActions from './payments.actions';
import * as appActions from '@store/app/app.actions';
import * as offersActions from '@store/offers/offers.actions';
import * as paymentSelectors from './payments.selectors';
import { EONBOARDING_STEPS } from '../../../core/enums/onboarding.enum';
import { IExtraData, IPutRecord } from '@app/core/models/app.model';
import { OffersFacade } from '@app/facades/offers.facade';
import { StorageService } from '@app/data/browser/storage.service';
import { CustomerFacade } from '../../../facades/customer.facade';
import * as customerActions from '@store/customer/customer.actions';
import { ICustomerCard } from '../../../core/models/customer.model';
import { ErrorService } from '@app/core/services/error.service';
import { IErrorComponentData, EERROR_PAGE_TYPES } from '../../../core/models/error.model';
import { ERROR_COMPONENT_CONSTANTS } from '../../services/error/error.constants';

@Injectable()
export class PaymentsEffects {
  constructor(
    private actions$: Actions,
    private _store: Store,
    private _paymentsAPI: PaymentsAPI,
    private _utilService: UtilService,
    private _openpayService: OpenpayService,
    private _mapErrorService: MapErrorService,
    private _errorService: ErrorService,
    private _paymentService: PaymentService,
    private _storageService: StorageService,
    private _errorFacade: ErrorFacade,
    private _appFacade: AppFacade,
    private _paymentFacade: PaymentsFacade,
    private _offersFacade: OffersFacade,
    private _appAPI: AppAPI,
    private _customerFacade: CustomerFacade
  ) {}

  public createCardToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentActions.createCardToken),
      switchMap(({ creditCardInfo, paymentParams }) =>
        this._openpayService.createToken(creditCardInfo).pipe(
          tap((createCardToken: CreateCardToken) => {
            const maskedCreditCardInfo = {
              ...creditCardInfo,
              card_date: {
                month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
                year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
              },
              card_number: createCardToken.card.card_number,
              expiration_year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
              expiration_month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
              cvv2: this._utilService.maskedValue(creditCardInfo.cvv2, 0),
              holder_name: creditCardInfo.holder_name,
            };
            const maskedCreditCard: ICreditCard = {
              token: this._utilService.maskedValue(createCardToken.id, 0),
              name: this._utilService.maskedValue(createCardToken.card.holder_name, 0),
              brand: createCardToken.card.brand,
              masked_number: this._utilService.maskedValue(createCardToken.card.card_number, 4),
              device_session_id: this._utilService.maskedValue(paymentParams.device_session_id, 4),
              transaction_id: paymentParams.transaction_id,
              is_available_for_purchase: creditCardInfo.nextPayments,
              response_time: createCardToken.response_time,
            };
            const data: IPutRecord = EVENTS.CARD_REGISTER.REQUEST;
            const extra_data: IExtraData = {
              request: maskedCreditCardInfo,
              response: maskedCreditCard,
            };
            this._appFacade.sendPutRecord(data, extra_data);
          }),
          map((createCardToken: CreateCardToken) => {
            const creditCard: ICreditCard = {
              token: createCardToken.id,
              name: createCardToken.card.holder_name,
              brand: createCardToken.card.brand,
              masked_number: createCardToken.card.card_number,
              device_session_id: paymentParams.device_session_id,
              transaction_id: paymentParams.transaction_id,
              is_available_for_purchase: creditCardInfo.nextPayments,
            };
            const maskedCreditCard: ICreditCard = {
              token: this._utilService.maskedValue(createCardToken.id, 0),
              name: createCardToken.card.holder_name,
              brand: createCardToken.card.brand,
              masked_number: createCardToken.card.card_number,
              device_session_id: this._utilService.maskedValue(paymentParams.device_session_id, 4),
              transaction_id: paymentParams.transaction_id,
              is_available_for_purchase: creditCardInfo.nextPayments,
              response_time: createCardToken.response_time,
            };
            const event: IPutRecord = EVENTS.CARD_REGISTER.EVENT;
            const extraData: IExtraData = { response: maskedCreditCard };
            this._appFacade.sendPutRecord(event, extraData);
            return paymentActions.successCreateCardToken({ creditCard });
          }),
          catchError((error) => {
            const maskedCreditCardInfo = {
              ...creditCardInfo,
              card_date: {
                month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
                year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
              },
              card_number: this._utilService.maskedValue(creditCardInfo.card_number, 4),
              expiration_year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
              expiration_month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
              cvv2: this._utilService.maskedValue(creditCardInfo.cvv2, 0),
              holder_name: this._utilService.maskedValue(creditCardInfo.holder_name, 0),
            };
            const data: IPutRecord = EVENTS.CARD_REGISTER.REQUEST;
            const extraData: IExtraData = {
              request: maskedCreditCardInfo,
              response: {
                error: error?.data
                  ? Object.keys(error?.data).length > 0
                    ? error?.data
                    : error
                  : error,
              },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(paymentActions.failureCreateCardToken({ error }));
          })
        )
      )
    )
  );

  public successCreateCardToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.successCreateCardToken),
        tap(() => this._store.dispatch(paymentActions.createCreditCard()))
      ),
    { dispatch: false }
  );

  public failureCreateCardToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.failureCreateCardToken),
        tap((error) => {
          if (!this._errorService.existSpecialCodes(error.error)) {
            return this._errorFacade.handleError(error.error, EERROR_TYPE.COMPONENT);
          }

          return this._appFacade.killPurchaseWithoutRedirect(error.error);
        })
      ),
    { dispatch: false }
  );

  public createCreditCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentActions.createCreditCard),
      withLatestFrom(this._store.select(paymentSelectors.selectCreditCard)),
      switchMap(([_, creditCard]) => {
        let startTime = new Date().getTime();
        return this._paymentsAPI.createCreditCard(creditCard).pipe(
          tap(() => {
            let endTime = new Date().getTime();
            let responseTime = (endTime - startTime) / 1000;
            const maskedCreditCard = {
              ...creditCard,
              token: this._utilService.maskedValue(creditCard.token, 4),
              name: this._utilService.maskedValue(creditCard.name, 0),
              brand: this._utilService.maskedValue(creditCard.brand, 0),
              masked_number: this._utilService.maskedValue(creditCard.masked_number, 0),
              device_session_id: this._utilService.maskedValue(creditCard.device_session_id, 0),
              is_available_for_purchase: creditCard.is_available_for_purchase,
            };
            const data: IPutRecord = EVENTS.CARD_REGISTER.REQUEST_CARD;
            const extraData: IExtraData = {
              request: maskedCreditCard,
              response: { response_time: responseTime },
            };
            this._appFacade.sendPutRecord(data, extraData);
          }),
          map(() => paymentActions.successCreateCreditCard()),
          catchError((error) => {
            let endTime = new Date().getTime();
            let responseTime = (endTime - startTime) / 1000;
            const maskedCreditCard = {
              ...creditCard,
              token: this._utilService.maskedValue(creditCard.token, 4),
              name: this._utilService.maskedValue(creditCard.name, 0),
              brand: this._utilService.maskedValue(creditCard.brand, 0),
              masked_number: this._utilService.maskedValue(creditCard.masked_number, 0),
              device_session_id: this._utilService.maskedValue(creditCard.device_session_id, 0),
              is_available_for_purchase: creditCard.is_available_for_purchase,
            };
            const data: IPutRecord = EVENTS.CARD_REGISTER.REQUEST_CARD;
            const extraData: IExtraData = {
              request: maskedCreditCard,
              response: { error: { ...(error?.error ?? error) }, response_time: responseTime },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(paymentActions.failureCreateCreditCard(error));
          })
        );
      })
    )
  );

  public successCreateCreditCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.successCreateCreditCard),
        tap(() => {
          this._storageService.removeItem('transactions');
          this._storageService.removeItem('customerValidation');
          this._store.dispatch(paymentActions.createPayments());
        })
      ),
    { dispatch: false }
  );

  public failureCreateCreditCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.failureCreateCreditCard),
        withLatestFrom(
          this._store.select(appSelectors.selectSession),
          this._store.select(appSelectors.selectTransactions),
          this._store.select(customerSelectors.selectCustomerValidation)
        ),
        tap(([error, session, transactions, customerValidation]) => {
          this._storageService.setItem('transactions', transactions);
          this._storageService.setItem('customerValidation', customerValidation);
          const { is_customer } = session;
          const errorCode = this._mapErrorService.mapError(error);
          if (!this._errorService.existSpecialCodes(errorCode)) {
            return this._errorFacade.handleError(errorCode, EERROR_TYPE.COMPONENT, is_customer);
          }
        })
      ),
    { dispatch: false }
  );

  public createPayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentActions.createPayments),
      delay(500),
      withLatestFrom(
        this._store.select(paymentSelectors.selectPaymentParams),
        this._store.select(customerSelectors.selectCustomerValidation)
      ),
      switchMap(([_, paymentParams, { has_card }]) => {
        let startTime = new Date().getTime();
        if (has_card) this._paymentService.handlePaymentIfHasCard();
        return this._paymentsAPI.createPayments(paymentParams).pipe(
          tap((paymentResponse) => {
            let endTime = new Date().getTime();
            let responseTime = (endTime - startTime) / 1000;
            const data: IPutRecord = EVENTS.PAYMENTS.REQUEST;
            const extraData: IExtraData = {
              request: paymentParams,
              response: { ...paymentResponse, response_time: responseTime },
            };
            this._appFacade.sendPutRecord(data, extraData);
          }),
          mergeMap((paymentResponse: IPaymentResponse) => {
            if (paymentResponse && paymentResponse.redirect_url) {
              const redirect_url = paymentResponse.redirect_url;
              return of(paymentActions.open3dsRedirection({ redirect_url, paymentResponse }));
            } else {
              return of(paymentActions.successCreatePayments({ paymentResponse }));
            }
          }),
          catchError((error) => {
            let endTime = new Date().getTime();
            let responseTime = (endTime - startTime) / 1000;
            const data: IPutRecord = EVENTS.PAYMENTS.REQUEST;
            const extraData: IExtraData = {
              request: paymentParams,
              response: { error: { ...(error?.error ?? error) }, response_time: responseTime },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(paymentActions.failureCreatePayments({ error }));
          })
        );
      })
    )
  );

  public successCreatePayments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.successCreatePayments),
        tap(() => {
          this._appFacade.nextStep();
          this._appFacade.getTransactionsWithOutId();
          const route: string = APP_ROUTES.PAYMENT_STATE;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public failureCreatePayments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.failureCreatePayments),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([{ error }, session]) => {
          const event = EVENTS.PAYMENT_REJECTION.VISIT;
          const { is_customer } = session;
          const componentData: IErrorComponentData =
            ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_12];

          this._appFacade.sendPutRecord(event);
          return this._appFacade.killPurchaseWithoutRedirect(error, is_customer, componentData);
        })
      ),
    { dispatch: false }
  );

  public open3dsRedirection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.open3dsRedirection),
        withLatestFrom(
          this._appFacade.step$,
          this._appFacade.transactions$,
          this._offersFacade.previewOffer$,
          this._customerFacade.customerCard$,
          this._customerFacade.customerValidation$
        ),
        tap(
          ([
            { redirect_url, paymentResponse },
            step,
            transactions,
            previewOffer,
            customerCard,
            customerValidation,
          ]) => {
            const hadValidLoan = !!customerCard ? customerCard?.had_valid_loan : false;
            const newStep: number = hadValidLoan
              ? EONBOARDING_STEPS.PAYMENT_SUCCESS
              : EONBOARDING_STEPS.PAYMENT_METHOD;

            this._storageService.setItem('step', newStep);
            this._storageService.setItem('transactions', transactions);
            this._storageService.setItem('previewOffer', previewOffer);
            this._storageService.setItem('paymentResponse', paymentResponse);
            this._storageService.setItem('customerValidation', customerValidation);
            this._storageService.setItem('customerCard', customerCard);
            this._utilService.redirectToUrl(redirect_url);
          }
        )
      ),
    { dispatch: false }
  );

  public redirectoToPaymentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentActions.redirectToPaymentStatus),
      switchMap(() => {
        const step = this._storageService.getItem('step');
        const transactions = this._storageService.getItem('transactions');
        const previewOffer = this._storageService.getItem('previewOffer');
        const paymentResponse = this._storageService.getItem('paymentResponse');
        const customerValidation = this._storageService.getItem('customerValidation');
        const customerCard = this._storageService.getItem('customerCard');

        if (step === EONBOARDING_STEPS.PAYMENT_METHOD) {
          this._paymentFacade.setLoading(false);
        }

        return [
          appActions.setNewStep({ step }),
          appActions.setTransactions({ transactions }),
          offersActions.setPreviewOffer({ previewOffer }),
          paymentActions.successCreatePayments({ paymentResponse }),
          customerActions.setCustomerValidate({ validation: customerValidation }),
          customerActions.successGetCustomerCard({ cardInfo: customerCard }),
        ];
      })
    )
  );

  public getCreditCardType$ = createEffect(() => {
    let startTime: number;
    return this.actions$.pipe(
      ofType(paymentActions.getCreditCardType),
      switchMap(
        ({
          creditCardInfo,
          paymentParams,
        }: {
          creditCardInfo: CreditCardInfo;
          paymentParams: IPaymentParams;
        }) => {
          startTime = new Date().getTime();
          const bines = this._paymentService.getBinesFromCreditCardInfo(creditCardInfo);
          return this._paymentsAPI.getCreditCardType(bines).pipe(
            map(({ cardType }: CreditCardTypeResponse) => {
              if (cardType === CARD_TYPE.DEBIT) {
                // adapter to prevent debit card and show error.
                throw new HttpErrorResponse({
                  error: {
                    errors: [{ message: ERROR_CONSTANTS.ERRORS.MESSAGE_BACKEND.DEBIT_CARD }],
                  },
                  statusText: ERROR_CONSTANTS.ERRORS.MESSAGE_BACKEND.DEBIT_CARD,
                });
              }
              let endTime = new Date().getTime();
              let responseTime = (endTime - startTime) / 1000;
              const maskedCreditCardInfo = {
                ...creditCardInfo,
                card_date: {
                  month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
                  year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
                },
                holder_name: this._utilService.maskedValue(creditCardInfo.holder_name, 0),
                card_number: this._utilService.maskedValue(creditCardInfo.card_number, 4),
                expiration_year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
                expiration_month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
                cvv2: this._utilService.maskedValue(creditCardInfo.cvv2, 0),
              };
              const data: IPutRecord = EVENTS.PROMOTIONS.REQUEST;
              const extra_data: IExtraData = {
                request: maskedCreditCardInfo,
                response: { cardType, response_time: responseTime },
              };
              this._appFacade.sendPutRecord(data, extra_data);
              return paymentActions.createCardToken({ creditCardInfo, paymentParams });
            }),
            catchError((error) => {
              let endTime = new Date().getTime();
              let responseTime = (endTime - startTime) / 1000;
              const maskedCreditCardInfo = {
                ...creditCardInfo,
                card_date: {
                  month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
                  year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
                },
                holder_name: this._utilService.maskedValue(creditCardInfo.holder_name, 0),
                card_number: this._utilService.maskedValue(creditCardInfo.card_number, 4),
                expiration_year: this._utilService.maskedValue(creditCardInfo.expiration_year, 0),
                expiration_month: this._utilService.maskedValue(creditCardInfo.expiration_month, 0),
                cvv2: this._utilService.maskedValue(creditCardInfo.cvv2, 0),
              };
              const data: IPutRecord = EVENTS.PROMOTIONS.REQUEST;
              const extra_data: IExtraData = {
                request: maskedCreditCardInfo,
                response: { error: { ...(error?.error ?? error) }, response_time: responseTime },
              };
              this._appFacade.sendPutRecord(data, extra_data);
              return of(paymentActions.failureGetCreditCardType({ error }));
            })
          );
        }
      )
    );
  });

  public failureGetCreditCardType$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.failureGetCreditCardType),
        tap(({ error }) => {
          const messageError = this._mapErrorService.mapError(error);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public createPaymentIfHasCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.createPaymentIfHasCard),
        withLatestFrom(this._store.select(appSelectors.selectTransactions)),
        tap(([{ paymentParams }, { id }]) => {
          const params: IPaymentParams = { ...paymentParams, transaction_id: id };
          this._appFacade.nextStep();
          this._paymentFacade.setPaymentsParams(params);
          return this._paymentFacade.createPayments(params);
        })
      ),
    { dispatch: false }
  );

  public redirectToMerchant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.redirectToMerchant),
        withLatestFrom(this._store.select(appSelectors.selectRedirectUrl)),
        tap(([_, redirectUrl]) => {
          if (!redirectUrl) {
            return this._appFacade.killPurchase();
          }
          const event: IPutRecord = EVENTS.APP.KILL_PURCHASE;
          this._appFacade.sendPutRecord(event);
          return this._utilService.redirectToUrl(redirectUrl);
        })
      ),
    { dispatch: false }
  );

  public redirectToMerchantWithLoop$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.redirectToMerchantWithLoop),
        delay(15000),
        withLatestFrom(this._store.select(appSelectors.selectTransactions)),
        tap(([_, { redirect_url }]) => {
          const url: string = redirect_url ?? ENV.portal_url;
          this._utilService.redirectToUrl(url);
        })
      ),
    { dispatch: false }
  );

  public redirecToPersonalInfo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.redirecToPersonalInfo),
        tap((_) => {
          const route: string = ROUTES.ONBOARDING;
          this._appFacade.setNewStep(EONBOARDING_STEPS.USER_INFO);
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public setIsLoadingPayment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.setIsLoadingPayment),
        tap(({ isLoading, step }) => {
          const route: string =
            step === EONBOARDING_STEPS.PAYMENT_SUCCESS
              ? APP_ROUTES.PAYMENT_PLAN
              : APP_ROUTES.PAYMENT_METHOD;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public setPaymentErrorCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(paymentActions.setPaymentErrorCode),
        withLatestFrom(
          this._store.select(paymentSelectors.selectPaymentResponse),
          this._store.select(customerSelectors.selectCustomerCard)
        ),
        switchMap(([_, paymentResponse, customerCard]) =>
          this._appAPI.setPaymentErrorCode().pipe(
            tap((_) => {
              if (paymentResponse && !paymentResponse.redirect_url) {
                const newStep: number = customerCard?.had_valid_loan
                  ? EONBOARDING_STEPS.PAYMENT_SUCCESS
                  : EONBOARDING_STEPS.PAYMENT_METHOD;
                this._customerFacade.updateIsLoadingPreLoan(false);
                return this._paymentFacade.setIsLoadingPayment(false, newStep);
              }

              const transactions = this._storageService.getItem('transactions');
              const previewOffer = this._storageService.getItem('previewOffer');
              const customerValidation = this._storageService.getItem('customerValidation');
              const customerCardStorage: ICustomerCard =
                this._storageService.getItem('customerCard');
              const had_valid_loan = customerCardStorage
                ? customerCardStorage?.had_valid_loan
                : false;
              const newStep: number = had_valid_loan
                ? EONBOARDING_STEPS.PAYMENT_SUCCESS
                : EONBOARDING_STEPS.PAYMENT_METHOD;
              this._appFacade.setNewStep(newStep);
              this._appFacade.setTransactions(transactions);
              this._offersFacade.setPreviewOffer(previewOffer);
              this._customerFacade.successGetCustomerCard(customerCard);
              this._customerFacade.setCustomerValidate(customerValidation);
              this._customerFacade.updateIsLoadingPreLoan(false);
              return this._paymentFacade.setIsLoadingPayment(false, newStep);
            }),
            catchError((error) => of(paymentActions.failureCreateCreditCard({ error })))
          )
        )
      ),
    { dispatch: false }
  );
}
